/**
 * characterTemplates.ts — Composable character sprite template system.
 *
 * Defines base body frame templates (16x32) using pixel-type markers,
 * then composites hair/clothing/accessory overlays and maps to hex colors
 * based on per-member CharacterConfig. Produces CharacterSprites compatible
 * with the existing office renderer pipeline.
 *
 * Zero external dependencies. No asset files required.
 */

import { flipSpriteHorizontal } from './spriteData';
import type { CharacterSprites } from './spriteData';
import type { SpriteData } from './types';
import { Direction } from './types';

// ════════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════════

export type HairStyle = 'short' | 'long' | 'ponytail' | 'bun' | 'curly' | 'buzz' | 'mohawk' | 'afro';
export type ShirtStyle = 'tee' | 'collared' | 'hoodie' | 'blazer';
export type Accessory = 'glasses' | 'headphones' | 'hat' | 'earrings' | null;

export interface CharacterConfig {
  skinTone: number;       // 0-5 index into SKIN_PALETTES
  hairStyle: HairStyle;
  hairColor: string;      // hex
  shirtStyle: ShirtStyle;
  shirtColor: string;     // hex
  pantsColor: string;     // hex
  shoeColor: string;      // hex
  accessory: Accessory;
}

interface SkinPalette {
  base: string;
  shadow: string;
  highlight: string;
}

// ════════════════════════════════════════════════════════════════
// Color Palettes
// ════════════════════════════════════════════════════════════════

export const SKIN_PALETTES: SkinPalette[] = [
  { base: '#FFDBB4', shadow: '#E8B88A', highlight: '#FFE8CC' },  // 0: light
  { base: '#F1C27D', shadow: '#D4A76A', highlight: '#FFD89B' },  // 1: light-medium
  { base: '#C68642', shadow: '#A06B30', highlight: '#D9A060' },  // 2: medium
  { base: '#8D5524', shadow: '#6B3F1B', highlight: '#A8693A' },  // 3: medium-dark
  { base: '#5C3310', shadow: '#3D210A', highlight: '#7A4A1E' },  // 4: dark
  { base: '#3B2011', shadow: '#2A160C', highlight: '#553318' },  // 5: deep
];

/** Darken a hex color by a factor (0-1, lower = darker) */
function darkenHex(hex: string, factor: number): string {
  const r = Math.round(parseInt(hex.slice(1, 3), 16) * factor);
  const g = Math.round(parseInt(hex.slice(3, 5), 16) * factor);
  const b = Math.round(parseInt(hex.slice(5, 7), 16) * factor);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
}

/** Lighten a hex color by a factor (>1 = lighter) */
function lightenHex(hex: string, factor: number): string {
  const r = Math.min(255, Math.round(parseInt(hex.slice(1, 3), 16) * factor));
  const g = Math.min(255, Math.round(parseInt(hex.slice(3, 5), 16) * factor));
  const b = Math.min(255, Math.round(parseInt(hex.slice(5, 7), 16) * factor));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
}

// ════════════════════════════════════════════════════════════════
// Pixel Type Markers
// ════════════════════════════════════════════════════════════════
//
// Each pixel in a template is a 1-2 char string:
//   ''   = transparent
//   'O'  = outline (dark, near-black)
//   'S'  = skin base
//   'Ss' = skin shadow
//   'Sh' = skin highlight
//   'H'  = hair base
//   'Hd' = hair dark/shadow
//   'E'  = eye color (white)
//   'P'  = pupil (black)
//   'M'  = mouth (dark skin shadow)
//   'C'  = clothing base
//   'Cd' = clothing dark/shadow
//   'Cl' = clothing light/highlight
//   'T'  = pants/trousers base
//   'Td' = pants dark/shadow
//   'F'  = footwear base
//   'Fd' = footwear dark
//   'A'  = accessory color (mapped per accessory type)

type PixelMarker = '' | 'O' | 'S' | 'Ss' | 'Sh' | 'H' | 'Hd' | 'E' | 'P' | 'M' | 'C' | 'Cd' | 'Cl' | 'T' | 'Td' | 'F' | 'Fd' | 'A';

/** Parse a template string into a PixelMarker 2D grid */
function parseTemplate(rows: string[]): PixelMarker[][] {
  return rows.map(row => {
    const markers: PixelMarker[] = [];
    let i = 0;
    while (i < row.length) {
      if (row[i] === '.') {
        markers.push('');
        i++;
      } else if (i + 1 < row.length && (row[i + 1] === 's' || row[i + 1] === 'h' || row[i + 1] === 'd' || row[i + 1] === 'l')) {
        markers.push((row[i] + row[i + 1]) as PixelMarker);
        i += 2;
      } else {
        markers.push(row[i] as PixelMarker);
        i++;
      }
    }
    // Pad to 16 pixels wide
    while (markers.length < 16) markers.push('');
    return markers.slice(0, 16);
  });
}

// ════════════════════════════════════════════════════════════════
// Base Body Frame Templates (DOWN direction)
// ════════════════════════════════════════════════════════════════
//
// 16 wide × 32 tall. Character occupies roughly rows 2-29.
// These templates use 'short' hair, 'tee' shirt as defaults.
// Overlays modify specific pixels for other hair/shirt/accessory styles.

// ── WALK FRAME 0 (standing neutral, facing down) ──
const DOWN_WALK_0 = parseTemplate([
  // Row 0-1: empty + hair top
  '................',
  '......HHHH......',
  // Row 2-3: hair crown
  '.....HHHdHH.....',
  '....HHHHHdHH....',
  // Row 4-5: hair sides + forehead
  '....HShShShH....',
  '....HSShShSH....',
  // Row 6: eyes
  '....SE.PE.PS....',
  // Row 7: nose
  '.....SSShSS.....',
  // Row 8: mouth
  '.....SSMSSS.....',
  // Row 9: chin
  '......SSSS......',
  // Row 10: neck
  '.......SS.......',
  // Row 11-12: shoulders/collar
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  // Row 13-14: upper torso
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  // Row 15-16: mid torso + arms
  '...SsCCCCCCsS...',
  '...S.CCCCCC.S...',
  // Row 17: waist
  '....CdCCCCCd....',
  // Row 18-19: belt/upper pants
  '.....TTTTTT.....',
  '.....TTTTTT.....',
  // Row 20-22: pants legs
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  // Row 23-24: lower legs
  '.....TdTTd......',
  '.....TT.TT......',
  // Row 25-26: ankles
  '.....TT.TT......',
  '.....FF.FF......',
  // Row 27-28: shoes
  '....FFF.FFF.....',
  '....FFd.FFd.....',
  // Row 29-31: empty
  '................',
  '................',
  '................',
]);

// ── WALK FRAME 1 (left step, facing down) ──
const DOWN_WALK_1 = parseTemplate([
  '................',
  '......HHHH......',
  '.....HHHdHH.....',
  '....HHHHHdHH....',
  '....HShShShH....',
  '....HSShShSH....',
  '....SE.PE.PS....',
  '.....SSShSS.....',
  '.....SSMSSS.....',
  '......SSSS......',
  '.......SS.......',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  '...SsCCCCCCsS...',
  '...S.CCCCCC.S...',
  '....CdCCCCCd....',
  '.....TTTTTT.....',
  '.....TTTTTT.....',
  '....TT..TT......',  // left leg forward
  '....TT..TT......',
  '.....TT.TT......',
  '.....Td..Td.....',
  '....TT...TT.....',
  '...FF....TT.....',
  '...FFd...FF.....',
  '.........FFd....',
  '................',
  '................',
  '................',
  '................',
]);

// ── WALK FRAME 2 (right step, facing down) ──
const DOWN_WALK_2 = parseTemplate([
  '................',
  '......HHHH......',
  '.....HHHdHH.....',
  '....HHHHHdHH....',
  '....HShShShH....',
  '....HSShShSH....',
  '....SE.PE.PS....',
  '.....SSShSS.....',
  '.....SSMSSS.....',
  '......SSSS......',
  '.......SS.......',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  '...SsCCCCCCsS...',
  '...S.CCCCCC.S...',
  '....CdCCCCCd....',
  '.....TTTTTT.....',
  '.....TTTTTT.....',
  '......TT..TT....',  // right leg forward
  '......TT..TT....',
  '.....TT.TT......',
  '.....Td..Td.....',
  '.....TT...TT....',
  '.....TT....FF...',
  '.....FF...FFd...',
  '....FFd.........',
  '................',
  '................',
  '................',
  '................',
]);

// ── TYPING FRAME 0 (facing down, arms forward-left) ──
const DOWN_TYPE_0 = parseTemplate([
  '................',
  '......HHHH......',
  '.....HHHdHH.....',
  '....HHHHHdHH....',
  '....HShShShH....',
  '....HSShShSH....',
  '....SE.PE.PS....',
  '.....SSShSS.....',
  '.....SSMSSS.....',
  '......SSSS......',
  '.......SS.......',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  '..SsCCCCCCCCsS..',
  '.SS.CCCCCCCC.SS.',
  '.S..CdCCCCCd..S.',
  '.Ss..TTTTTT..sS.',
  '......TTTTTT....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......FF.FF.....',
  '.....FFF.FFF....',
  '.....FFd.FFd....',
  '................',
  '................',
  '................',
  '................',
]);

// ── TYPING FRAME 1 (facing down, arms forward-right) ──
const DOWN_TYPE_1 = parseTemplate([
  '................',
  '......HHHH......',
  '.....HHHdHH.....',
  '....HHHHHdHH....',
  '....HShShShH....',
  '....HSShShSH....',
  '....SE.PE.PS....',
  '.....SSShSS.....',
  '.....SSMSSS.....',
  '......SSSS......',
  '.......SS.......',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  '.SsCCCCCCCCCCsS.',
  '.SS.CCCCCCCC.SS.',
  '..S.CdCCCCCd.S..',
  '..Ss.TTTTTT.sS..',
  '......TTTTTT....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......FF.FF.....',
  '.....FFF.FFF....',
  '.....FFd.FFd....',
  '................',
  '................',
  '................',
  '................',
]);

// ── READING FRAME 0 (facing down, one arm up holding) ──
const DOWN_READ_0 = parseTemplate([
  '................',
  '......HHHH......',
  '.....HHHdHH.....',
  '....HHHHHdHH....',
  '....HShShShH....',
  '....HSShShSH....',
  '....SE.PE.PS....',
  '.....SSShSS.....',
  '.....SSMSSS.....',
  '......SSSS......',
  '.......SS.......',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  '...SsCCCCCCsS...',
  '...S.CCCCCC.SS..',
  '....CdCCCCCdSS..',
  '.....TTTTTT.S...',
  '.....TTTTTT.....',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....FF.FF......',
  '....FFF.FFF.....',
  '....FFd.FFd.....',
  '................',
  '................',
  '................',
  '................',
]);

// ── READING FRAME 1 (facing down, arm slightly shifted) ──
const DOWN_READ_1 = parseTemplate([
  '................',
  '......HHHH......',
  '.....HHHdHH.....',
  '....HHHHHdHH....',
  '....HShShShH....',
  '....HSShShSH....',
  '....SE.PE.PS....',
  '.....SSShSS.....',
  '.....SSMSSS.....',
  '......SSSS......',
  '.......SS.......',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  '...SsCCCCCCsS...',
  '...S.CCCCCC..SS.',
  '....CdCCCCCd.SS.',
  '.....TTTTTT..S..',
  '.....TTTTTT.....',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....FF.FF......',
  '....FFF.FFF.....',
  '....FFd.FFd.....',
  '................',
  '................',
  '................',
  '................',
]);

// ════════════════════════════════════════════════════════════════
// UP direction frames (back view — no face details)
// ════════════════════════════════════════════════════════════════

const UP_WALK_0 = parseTemplate([
  '................',
  '......HHHH......',
  '.....HHHHHH.....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '.....HHHHHH.....',
  '.....HHHHHH.....',
  '......HHHH......',
  '.......SS.......',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  '...SsCCCCCCsS...',
  '...S.CCCCCC.S...',
  '....CdCCCCCd....',
  '.....TTTTTT.....',
  '.....TTTTTT.....',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TdTTd......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....FF.FF......',
  '....FFF.FFF.....',
  '....FFd.FFd.....',
  '................',
  '................',
  '................',
]);

const UP_WALK_1 = parseTemplate([
  '................',
  '......HHHH......',
  '.....HHHHHH.....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '.....HHHHHH.....',
  '.....HHHHHH.....',
  '......HHHH......',
  '.......SS.......',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  '...SsCCCCCCsS...',
  '...S.CCCCCC.S...',
  '....CdCCCCCd....',
  '.....TTTTTT.....',
  '.....TTTTTT.....',
  '....TT..TT......',
  '....TT..TT......',
  '.....TT.TT......',
  '.....Td..Td.....',
  '....TT...TT.....',
  '...FF....TT.....',
  '...FFd...FF.....',
  '.........FFd....',
  '................',
  '................',
  '................',
  '................',
]);

const UP_WALK_2 = parseTemplate([
  '................',
  '......HHHH......',
  '.....HHHHHH.....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '.....HHHHHH.....',
  '.....HHHHHH.....',
  '......HHHH......',
  '.......SS.......',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  '...SsCCCCCCsS...',
  '...S.CCCCCC.S...',
  '....CdCCCCCd....',
  '.....TTTTTT.....',
  '.....TTTTTT.....',
  '......TT..TT....',
  '......TT..TT....',
  '.....TT.TT......',
  '.....Td..Td.....',
  '.....TT...TT....',
  '.....TT....FF...',
  '.....FF...FFd...',
  '....FFd.........',
  '................',
  '................',
  '................',
  '................',
]);

const UP_TYPE_0 = parseTemplate([
  '................',
  '......HHHH......',
  '.....HHHHHH.....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '.....HHHHHH.....',
  '.....HHHHHH.....',
  '......HHHH......',
  '.......SS.......',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  '..SsCCCCCCCCsS..',
  '.SS.CCCCCCCC.SS.',
  '.S..CdCCCCCd..S.',
  '.Ss..TTTTTT..sS.',
  '......TTTTTT....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......FF.FF.....',
  '.....FFF.FFF....',
  '.....FFd.FFd....',
  '................',
  '................',
  '................',
  '................',
]);

const UP_TYPE_1 = parseTemplate([
  '................',
  '......HHHH......',
  '.....HHHHHH.....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '.....HHHHHH.....',
  '.....HHHHHH.....',
  '......HHHH......',
  '.......SS.......',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  '.SsCCCCCCCCCCsS.',
  '.SS.CCCCCCCC.SS.',
  '..S.CdCCCCCd.S..',
  '..Ss.TTTTTT.sS..',
  '......TTTTTT....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......TT.TT.....',
  '......FF.FF.....',
  '.....FFF.FFF....',
  '.....FFd.FFd....',
  '................',
  '................',
  '................',
  '................',
]);

const UP_READ_0 = parseTemplate([
  '................',
  '......HHHH......',
  '.....HHHHHH.....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '.....HHHHHH.....',
  '.....HHHHHH.....',
  '......HHHH......',
  '.......SS.......',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  '...SsCCCCCCsS...',
  '...S.CCCCCC.SS..',
  '....CdCCCCCdSS..',
  '.....TTTTTT.S...',
  '.....TTTTTT.....',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....FF.FF......',
  '....FFF.FFF.....',
  '....FFd.FFd.....',
  '................',
  '................',
  '................',
  '................',
]);

const UP_READ_1 = parseTemplate([
  '................',
  '......HHHH......',
  '.....HHHHHH.....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '....HHHHHHHH....',
  '.....HHHHHH.....',
  '.....HHHHHH.....',
  '......HHHH......',
  '.......SS.......',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '...CCSsCCCsCC...',
  '...CCCCCCCCCC...',
  '...SsCCCCCCsS...',
  '...S.CCCCCC..SS.',
  '....CdCCCCCd.SS.',
  '.....TTTTTT..S..',
  '.....TTTTTT.....',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....TT.TT......',
  '.....FF.FF......',
  '....FFF.FFF.....',
  '....FFd.FFd.....',
  '................',
  '................',
  '................',
  '................',
]);

// ════════════════════════════════════════════════════════════════
// RIGHT direction frames (side view)
// ════════════════════════════════════════════════════════════════

const RIGHT_WALK_0 = parseTemplate([
  '................',
  '.......HHHH.....',
  '......HHHdHH....',
  '.....HHHHHdHH...',
  '.....HHHHHdHH...',
  '.....HSSSShHH...',
  '.....SSE.PSH....',
  '......SSShS.....',
  '......SSMSS.....',
  '.......SSS......',
  '.......SS.......',
  '......CCCCCC....',
  '.....CCCCCCCC...',
  '.....CCCCCsCC...',
  '.....CCCCCCCC...',
  '.....SsCCCCCC...',
  '......CCCCCC....',
  '......CdCCCd....',
  '.......TTTTT....',
  '.......TTTTT....',
  '.......TT.TT...',
  '.......TT.TT...',
  '.......TT.TT...',
  '.......Td.Td...',
  '.......TT.TT...',
  '.......TT.TT...',
  '.......FF.FF....',
  '......FFF.FFF...',
  '......FFd.FFd...',
  '................',
  '................',
  '................',
]);

const RIGHT_WALK_1 = parseTemplate([
  '................',
  '.......HHHH.....',
  '......HHHdHH....',
  '.....HHHHHdHH...',
  '.....HHHHHdHH...',
  '.....HSSSShHH...',
  '.....SSE.PSH....',
  '......SSShS.....',
  '......SSMSS.....',
  '.......SSS......',
  '.......SS.......',
  '......CCCCCC....',
  '.....CCCCCCCC...',
  '.....CCCCCsCC...',
  '.....CCCCCCCC...',
  '.....SsCCCCCC...',
  '......CCCCCC....',
  '......CdCCCd....',
  '.......TTTTT....',
  '.......TTTTT....',
  '......TT..TT....',
  '......TT..TT....',
  '.......TT.TT....',
  '.......Td..Td...',
  '......TT...TT...',
  '.....FF....TT...',
  '.....FFd...FF...',
  '...........FFd..',
  '................',
  '................',
  '................',
  '................',
]);

const RIGHT_WALK_2 = parseTemplate([
  '................',
  '.......HHHH.....',
  '......HHHdHH....',
  '.....HHHHHdHH...',
  '.....HHHHHdHH...',
  '.....HSSSShHH...',
  '.....SSE.PSH....',
  '......SSShS.....',
  '......SSMSS.....',
  '.......SSS......',
  '.......SS.......',
  '......CCCCCC....',
  '.....CCCCCCCC...',
  '.....CCCCCsCC...',
  '.....CCCCCCCC...',
  '.....SsCCCCCC...',
  '......CCCCCC....',
  '......CdCCCd....',
  '.......TTTTT....',
  '.......TTTTT....',
  '........TT..TT..',
  '........TT..TT..',
  '.......TT.TT....',
  '.......Td..Td...',
  '.......TT...TT..',
  '.......TT....FF.',
  '.......FF...FFd.',
  '......FFd.......',
  '................',
  '................',
  '................',
  '................',
]);

const RIGHT_TYPE_0 = parseTemplate([
  '................',
  '.......HHHH.....',
  '......HHHdHH....',
  '.....HHHHHdHH...',
  '.....HHHHHdHH...',
  '.....HSSSShHH...',
  '.....SSE.PSH....',
  '......SSShS.....',
  '......SSMSS.....',
  '.......SSS......',
  '.......SS.......',
  '......CCCCCC....',
  '.....CCCCCCCC...',
  '.....CCCCCsCC...',
  '.....CCCCCCCC...',
  '.....CCCCCCCC...',
  '.....SCCCCCCSS..',
  '......CdCCCdSS..',
  '.......TTTTT.S..',
  '.......TTTTT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......FF.FF....',
  '......FFF.FFF...',
  '......FFd.FFd...',
  '................',
  '................',
  '................',
  '................',
]);

const RIGHT_TYPE_1 = parseTemplate([
  '................',
  '.......HHHH.....',
  '......HHHdHH....',
  '.....HHHHHdHH...',
  '.....HHHHHdHH...',
  '.....HSSSShHH...',
  '.....SSE.PSH....',
  '......SSShS.....',
  '......SSMSS.....',
  '.......SSS......',
  '.......SS.......',
  '......CCCCCC....',
  '.....CCCCCCCC...',
  '.....CCCCCsCC...',
  '.....CCCCCCCC...',
  '.....CCCCCCCC...',
  '.....SCCCCCCS...',
  '......CdCCCdSS..',
  '.......TTTTTSS..',
  '.......TTTTT.S..',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......FF.FF....',
  '......FFF.FFF...',
  '......FFd.FFd...',
  '................',
  '................',
  '................',
  '................',
]);

const RIGHT_READ_0 = parseTemplate([
  '................',
  '.......HHHH.....',
  '......HHHdHH....',
  '.....HHHHHdHH...',
  '.....HHHHHdHH...',
  '.....HSSSShHH...',
  '.....SSE.PSH....',
  '......SSShS.....',
  '......SSMSS.....',
  '.......SSS......',
  '.......SS.......',
  '......CCCCCC....',
  '.....CCCCCCCC...',
  '.....CCCCCsCC...',
  '.....CCCCCCCC...',
  '.....SsCCCCCC...',
  '......CCCCCC.SS.',
  '......CdCCCd.SS.',
  '.......TTTTT.S..',
  '.......TTTTT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......FF.FF....',
  '......FFF.FFF...',
  '......FFd.FFd...',
  '................',
  '................',
  '................',
  '................',
]);

const RIGHT_READ_1 = parseTemplate([
  '................',
  '.......HHHH.....',
  '......HHHdHH....',
  '.....HHHHHdHH...',
  '.....HHHHHdHH...',
  '.....HSSSShHH...',
  '.....SSE.PSH....',
  '......SSShS.....',
  '......SSMSS.....',
  '.......SSS......',
  '.......SS.......',
  '......CCCCCC....',
  '.....CCCCCCCC...',
  '.....CCCCCsCC...',
  '.....CCCCCCCC...',
  '.....SsCCCCCC...',
  '......CCCCCC..SS',
  '......CdCCCd..SS',
  '.......TTTTT..S.',
  '.......TTTTT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......TT.TT....',
  '.......FF.FF....',
  '......FFF.FFF...',
  '......FFd.FFd...',
  '................',
  '................',
  '................',
  '................',
]);

// ════════════════════════════════════════════════════════════════
// Hair Style Overlays
// ════════════════════════════════════════════════════════════════
//
// Each overlay is a sparse list of pixel modifications applied
// AFTER the base template. Format: [row, col, marker].
// Directions: 'down' | 'up' | 'right' (left is flipped from right).

interface PixelMod {
  row: number;
  col: number;
  marker: PixelMarker;
}

type DirectionOverlay = {
  down: PixelMod[];
  up: PixelMod[];
  right: PixelMod[];
};

const HAIR_OVERLAYS: Record<HairStyle, DirectionOverlay> = {
  short: { down: [], up: [], right: [] }, // default — no changes needed

  long: {
    down: [
      // Extend hair down sides to row 12
      { row: 7, col: 3, marker: 'H' },  { row: 7, col: 10, marker: 'H' },
      { row: 8, col: 3, marker: 'H' },  { row: 8, col: 10, marker: 'H' },
      { row: 9, col: 3, marker: 'H' },  { row: 9, col: 10, marker: 'H' },
      { row: 10, col: 4, marker: 'H' }, { row: 10, col: 9, marker: 'H' },
      { row: 11, col: 3, marker: 'H' }, { row: 11, col: 10, marker: 'H' },
      { row: 12, col: 3, marker: 'Hd' }, { row: 12, col: 10, marker: 'Hd' },
    ],
    up: [
      // Long hair visible from back
      { row: 10, col: 5, marker: 'H' }, { row: 10, col: 8, marker: 'H' },
      { row: 11, col: 4, marker: 'H' }, { row: 11, col: 9, marker: 'H' },
      { row: 12, col: 4, marker: 'H' }, { row: 12, col: 9, marker: 'H' },
      { row: 13, col: 4, marker: 'Hd' }, { row: 13, col: 9, marker: 'Hd' },
    ],
    right: [
      { row: 7, col: 4, marker: 'H' },
      { row: 8, col: 4, marker: 'H' },
      { row: 9, col: 4, marker: 'H' },
      { row: 10, col: 5, marker: 'H' },
      { row: 11, col: 5, marker: 'H' },
      { row: 12, col: 5, marker: 'Hd' },
    ],
  },

  ponytail: {
    down: [
      // Small ponytail visible behind head
      { row: 0, col: 7, marker: 'H' }, { row: 0, col: 8, marker: 'H' },
    ],
    up: [
      // Ponytail visible from back — hangs down
      { row: 10, col: 6, marker: 'H' }, { row: 10, col: 7, marker: 'H' },
      { row: 11, col: 6, marker: 'H' }, { row: 11, col: 7, marker: 'H' },
      { row: 12, col: 6, marker: 'H' }, { row: 12, col: 7, marker: 'H' },
      { row: 13, col: 6, marker: 'Hd' }, { row: 13, col: 7, marker: 'Hd' },
    ],
    right: [
      // Ponytail hangs behind
      { row: 4, col: 3, marker: 'H' }, { row: 4, col: 4, marker: 'H' },
      { row: 5, col: 3, marker: 'H' },
      { row: 6, col: 3, marker: 'H' },
      { row: 7, col: 3, marker: 'H' },
      { row: 8, col: 3, marker: 'Hd' },
    ],
  },

  bun: {
    down: [
      // Bun on top
      { row: 0, col: 6, marker: 'H' }, { row: 0, col: 7, marker: 'H' },
      { row: 0, col: 8, marker: 'Hd' },
    ],
    up: [
      { row: 0, col: 6, marker: 'H' }, { row: 0, col: 7, marker: 'H' },
      { row: 0, col: 8, marker: 'Hd' },
    ],
    right: [
      { row: 0, col: 8, marker: 'H' }, { row: 0, col: 9, marker: 'H' },
      { row: 0, col: 10, marker: 'Hd' },
    ],
  },

  curly: {
    down: [
      // Wider, rounder hair silhouette
      { row: 1, col: 5, marker: 'H' },  { row: 1, col: 10, marker: 'H' },
      { row: 2, col: 3, marker: 'H' },  { row: 2, col: 11, marker: 'H' },
      { row: 3, col: 3, marker: 'H' },  { row: 3, col: 12, marker: 'H' },
      { row: 4, col: 3, marker: 'H' },  { row: 4, col: 11, marker: 'H' },
      { row: 5, col: 3, marker: 'H' },  { row: 5, col: 11, marker: 'H' },
      { row: 7, col: 3, marker: 'H' },  { row: 7, col: 11, marker: 'H' },
      { row: 8, col: 3, marker: 'H' },  { row: 8, col: 11, marker: 'H' },
      { row: 9, col: 4, marker: 'Hd' }, { row: 9, col: 10, marker: 'Hd' },
    ],
    up: [
      { row: 1, col: 5, marker: 'H' },  { row: 1, col: 10, marker: 'H' },
      { row: 2, col: 3, marker: 'H' },  { row: 2, col: 11, marker: 'H' },
      { row: 3, col: 3, marker: 'H' },  { row: 3, col: 12, marker: 'H' },
      { row: 7, col: 3, marker: 'H' },  { row: 7, col: 11, marker: 'H' },
      { row: 8, col: 3, marker: 'H' },  { row: 8, col: 11, marker: 'H' },
      { row: 9, col: 4, marker: 'Hd' }, { row: 9, col: 10, marker: 'Hd' },
    ],
    right: [
      { row: 1, col: 11, marker: 'H' },
      { row: 2, col: 12, marker: 'H' },
      { row: 3, col: 12, marker: 'H' },
      { row: 4, col: 12, marker: 'H' },
      { row: 5, col: 12, marker: 'H' },
      { row: 7, col: 4, marker: 'H' },
      { row: 8, col: 4, marker: 'H' },
      { row: 9, col: 5, marker: 'Hd' },
    ],
  },

  buzz: {
    down: [
      // Very short — reduce hair area, more skin showing on sides
      { row: 4, col: 4, marker: 'Sh' }, { row: 4, col: 9, marker: 'Sh' },
      { row: 5, col: 4, marker: 'Sh' }, { row: 5, col: 9, marker: 'Sh' },
    ],
    up: [
      { row: 4, col: 4, marker: 'Hd' }, { row: 4, col: 9, marker: 'Hd' },
      { row: 5, col: 4, marker: 'Hd' }, { row: 5, col: 9, marker: 'Hd' },
    ],
    right: [],
  },

  mohawk: {
    down: [
      // Tall center spike
      { row: 0, col: 7, marker: 'H' }, { row: 0, col: 8, marker: 'H' },
      // Remove side hair
      { row: 4, col: 4, marker: 'Sh' }, { row: 4, col: 9, marker: 'Sh' },
      { row: 5, col: 4, marker: 'Sh' }, { row: 5, col: 9, marker: 'Sh' },
    ],
    up: [
      { row: 0, col: 7, marker: 'H' }, { row: 0, col: 8, marker: 'H' },
      { row: 4, col: 4, marker: 'Hd' }, { row: 4, col: 9, marker: 'Hd' },
    ],
    right: [
      { row: 0, col: 8, marker: 'H' }, { row: 0, col: 9, marker: 'H' },
    ],
  },

  afro: {
    down: [
      // Large round silhouette
      { row: 0, col: 5, marker: 'H' }, { row: 0, col: 6, marker: 'H' },
      { row: 0, col: 7, marker: 'H' }, { row: 0, col: 8, marker: 'H' },
      { row: 0, col: 9, marker: 'H' }, { row: 0, col: 10, marker: 'H' },
      { row: 1, col: 4, marker: 'H' }, { row: 1, col: 11, marker: 'H' },
      { row: 2, col: 3, marker: 'H' }, { row: 2, col: 12, marker: 'H' },
      { row: 3, col: 3, marker: 'H' }, { row: 3, col: 12, marker: 'H' },
      { row: 4, col: 3, marker: 'H' }, { row: 4, col: 11, marker: 'H' },
      { row: 5, col: 3, marker: 'H' }, { row: 5, col: 11, marker: 'H' },
      { row: 6, col: 3, marker: 'H' }, { row: 6, col: 11, marker: 'H' },
      { row: 7, col: 3, marker: 'H' }, { row: 7, col: 11, marker: 'H' },
      { row: 8, col: 3, marker: 'Hd' }, { row: 8, col: 11, marker: 'Hd' },
    ],
    up: [
      { row: 0, col: 5, marker: 'H' }, { row: 0, col: 10, marker: 'H' },
      { row: 1, col: 4, marker: 'H' }, { row: 1, col: 11, marker: 'H' },
      { row: 2, col: 3, marker: 'H' }, { row: 2, col: 12, marker: 'H' },
      { row: 3, col: 3, marker: 'H' }, { row: 3, col: 12, marker: 'H' },
      { row: 7, col: 3, marker: 'H' }, { row: 7, col: 11, marker: 'H' },
      { row: 8, col: 3, marker: 'Hd' }, { row: 8, col: 11, marker: 'Hd' },
    ],
    right: [
      { row: 0, col: 7, marker: 'H' }, { row: 0, col: 8, marker: 'H' },
      { row: 0, col: 9, marker: 'H' }, { row: 0, col: 10, marker: 'H' },
      { row: 0, col: 11, marker: 'H' },
      { row: 1, col: 12, marker: 'H' },
      { row: 2, col: 12, marker: 'H' }, { row: 2, col: 13, marker: 'H' },
      { row: 3, col: 13, marker: 'H' },
      { row: 4, col: 12, marker: 'H' },
      { row: 5, col: 12, marker: 'H' },
      { row: 6, col: 12, marker: 'H' },
      { row: 7, col: 4, marker: 'H' }, { row: 7, col: 12, marker: 'H' },
      { row: 8, col: 4, marker: 'Hd' }, { row: 8, col: 12, marker: 'Hd' },
    ],
  },
};

// ════════════════════════════════════════════════════════════════
// Shirt Style Overlays
// ════════════════════════════════════════════════════════════════

const SHIRT_OVERLAYS: Record<ShirtStyle, DirectionOverlay> = {
  tee: { down: [], up: [], right: [] }, // default

  collared: {
    down: [
      // Collar V-shape at neckline
      { row: 11, col: 6, marker: 'Cl' }, { row: 11, col: 9, marker: 'Cl' },
      { row: 12, col: 5, marker: 'Cl' }, { row: 12, col: 10, marker: 'Cl' },
    ],
    up: [
      { row: 11, col: 6, marker: 'Cl' }, { row: 11, col: 9, marker: 'Cl' },
    ],
    right: [
      { row: 11, col: 8, marker: 'Cl' },
    ],
  },

  hoodie: {
    down: [
      // Hood outline around head area
      { row: 9, col: 3, marker: 'Cd' }, { row: 9, col: 10, marker: 'Cd' },
      { row: 10, col: 3, marker: 'Cd' }, { row: 10, col: 10, marker: 'Cd' },
      // Kangaroo pocket
      { row: 16, col: 5, marker: 'Cd' }, { row: 16, col: 6, marker: 'Cd' },
      { row: 16, col: 7, marker: 'Cd' }, { row: 16, col: 8, marker: 'Cd' },
    ],
    up: [
      { row: 9, col: 4, marker: 'Cd' }, { row: 9, col: 9, marker: 'Cd' },
      { row: 10, col: 4, marker: 'Cd' }, { row: 10, col: 9, marker: 'Cd' },
    ],
    right: [
      { row: 9, col: 5, marker: 'Cd' },
      { row: 10, col: 5, marker: 'Cd' },
    ],
  },

  blazer: {
    down: [
      // Lapels
      { row: 11, col: 5, marker: 'Cd' }, { row: 11, col: 10, marker: 'Cd' },
      { row: 12, col: 4, marker: 'Cd' }, { row: 12, col: 11, marker: 'Cd' },
      { row: 13, col: 4, marker: 'Cd' }, { row: 13, col: 11, marker: 'Cd' },
      // Center button line
      { row: 14, col: 7, marker: 'Cl' },
      { row: 16, col: 7, marker: 'Cl' },
    ],
    up: [
      { row: 12, col: 4, marker: 'Cd' }, { row: 12, col: 11, marker: 'Cd' },
    ],
    right: [
      { row: 12, col: 5, marker: 'Cd' },
      { row: 13, col: 5, marker: 'Cd' },
    ],
  },
};

// ════════════════════════════════════════════════════════════════
// Accessory Overlays
// ════════════════════════════════════════════════════════════════

const ACCESSORY_OVERLAYS: Record<NonNullable<Accessory>, DirectionOverlay> = {
  glasses: {
    down: [
      // Glasses frames over eyes (row 6)
      { row: 6, col: 4, marker: 'A' },
      { row: 6, col: 7, marker: 'A' },
      { row: 6, col: 10, marker: 'A' },
    ],
    up: [],
    right: [
      { row: 6, col: 5, marker: 'A' },
      { row: 6, col: 10, marker: 'A' },
    ],
  },

  headphones: {
    down: [
      // Over-ear pads on sides
      { row: 3, col: 3, marker: 'A' },  { row: 3, col: 11, marker: 'A' },
      { row: 4, col: 3, marker: 'A' },  { row: 4, col: 11, marker: 'A' },
      { row: 5, col: 3, marker: 'A' },  { row: 5, col: 11, marker: 'A' },
      { row: 6, col: 3, marker: 'A' },  { row: 6, col: 11, marker: 'A' },
      // Headband on top
      { row: 1, col: 5, marker: 'A' },  { row: 1, col: 10, marker: 'A' },
    ],
    up: [
      { row: 3, col: 3, marker: 'A' },  { row: 3, col: 11, marker: 'A' },
      { row: 4, col: 3, marker: 'A' },  { row: 4, col: 11, marker: 'A' },
      { row: 5, col: 3, marker: 'A' },  { row: 5, col: 11, marker: 'A' },
      { row: 6, col: 3, marker: 'A' },  { row: 6, col: 11, marker: 'A' },
      { row: 1, col: 5, marker: 'A' },  { row: 1, col: 10, marker: 'A' },
    ],
    right: [
      { row: 3, col: 12, marker: 'A' },
      { row: 4, col: 12, marker: 'A' },
      { row: 5, col: 12, marker: 'A' },
      { row: 6, col: 12, marker: 'A' },
    ],
  },

  hat: {
    down: [
      // Wide brim hat replaces top hair
      { row: 0, col: 4, marker: 'A' }, { row: 0, col: 5, marker: 'A' },
      { row: 0, col: 6, marker: 'A' }, { row: 0, col: 7, marker: 'A' },
      { row: 0, col: 8, marker: 'A' }, { row: 0, col: 9, marker: 'A' },
      { row: 0, col: 10, marker: 'A' },
      { row: 1, col: 4, marker: 'A' }, { row: 1, col: 5, marker: 'A' },
      { row: 1, col: 6, marker: 'A' }, { row: 1, col: 7, marker: 'A' },
      { row: 1, col: 8, marker: 'A' }, { row: 1, col: 9, marker: 'A' },
      { row: 1, col: 10, marker: 'A' },
      { row: 2, col: 5, marker: 'A' }, { row: 2, col: 6, marker: 'A' },
      { row: 2, col: 7, marker: 'A' }, { row: 2, col: 8, marker: 'A' },
      { row: 2, col: 9, marker: 'A' },
    ],
    up: [
      { row: 0, col: 4, marker: 'A' }, { row: 0, col: 5, marker: 'A' },
      { row: 0, col: 6, marker: 'A' }, { row: 0, col: 7, marker: 'A' },
      { row: 0, col: 8, marker: 'A' }, { row: 0, col: 9, marker: 'A' },
      { row: 0, col: 10, marker: 'A' },
      { row: 1, col: 4, marker: 'A' }, { row: 1, col: 10, marker: 'A' },
      { row: 2, col: 5, marker: 'A' }, { row: 2, col: 9, marker: 'A' },
    ],
    right: [
      { row: 0, col: 6, marker: 'A' }, { row: 0, col: 7, marker: 'A' },
      { row: 0, col: 8, marker: 'A' }, { row: 0, col: 9, marker: 'A' },
      { row: 0, col: 10, marker: 'A' }, { row: 0, col: 11, marker: 'A' },
      { row: 1, col: 11, marker: 'A' }, { row: 1, col: 12, marker: 'A' },
      { row: 2, col: 11, marker: 'A' },
    ],
  },

  earrings: {
    down: [
      // Small dots on ear level
      { row: 7, col: 3, marker: 'A' },  { row: 7, col: 11, marker: 'A' },
    ],
    up: [],
    right: [
      { row: 7, col: 11, marker: 'A' },
    ],
  },
};

// ════════════════════════════════════════════════════════════════
// Color Mapping
// ════════════════════════════════════════════════════════════════

/** Map a PixelMarker to a hex color string based on CharacterConfig */
function markerToColor(marker: PixelMarker, config: CharacterConfig): string {
  const skin = SKIN_PALETTES[config.skinTone] ?? SKIN_PALETTES[0];

  switch (marker) {
    case '':   return '';
    case 'O':  return '#1A1A2E';
    case 'S':  return skin.base;
    case 'Ss': return skin.shadow;
    case 'Sh': return skin.highlight;
    case 'H':  return config.hairColor;
    case 'Hd': return darkenHex(config.hairColor, 0.7);
    case 'E':  return '#FFFFFF';
    case 'P':  return '#1A1A2E';
    case 'M':  return darkenHex(skin.shadow, 0.8);
    case 'C':  return config.shirtColor;
    case 'Cd': return darkenHex(config.shirtColor, 0.7);
    case 'Cl': return lightenHex(config.shirtColor, 1.3);
    case 'T':  return config.pantsColor;
    case 'Td': return darkenHex(config.pantsColor, 0.7);
    case 'F':  return config.shoeColor;
    case 'Fd': return darkenHex(config.shoeColor, 0.7);
    case 'A':  return '#333333'; // Default accessory color; could be extended
    default:   return '';
  }
}

// ════════════════════════════════════════════════════════════════
// Sprite Generation
// ════════════════════════════════════════════════════════════════

/** Deep clone a PixelMarker grid */
function cloneGrid(grid: PixelMarker[][]): PixelMarker[][] {
  return grid.map(row => [...row]);
}

/** Apply overlay modifications to a template grid */
function applyOverlay(grid: PixelMarker[][], mods: PixelMod[]): void {
  for (const mod of mods) {
    if (mod.row >= 0 && mod.row < grid.length && mod.col >= 0 && mod.col < grid[0].length) {
      grid[mod.row][mod.col] = mod.marker;
    }
  }
}

/** Convert a PixelMarker grid to a SpriteData (hex color 2D array) */
function gridToSprite(grid: PixelMarker[][], config: CharacterConfig): SpriteData {
  return grid.map(row => row.map(marker => markerToColor(marker, config)));
}

/** Generate a single frame with all overlays applied */
function generateFrame(
  baseTemplate: PixelMarker[][],
  config: CharacterConfig,
  direction: 'down' | 'up' | 'right',
): SpriteData {
  const grid = cloneGrid(baseTemplate);

  // Apply hair overlay
  const hairOverlay = HAIR_OVERLAYS[config.hairStyle];
  if (hairOverlay) {
    applyOverlay(grid, hairOverlay[direction]);
  }

  // Apply shirt overlay
  const shirtOverlay = SHIRT_OVERLAYS[config.shirtStyle];
  if (shirtOverlay) {
    applyOverlay(grid, shirtOverlay[direction]);
  }

  // Apply accessory overlay
  if (config.accessory) {
    const accOverlay = ACCESSORY_OVERLAYS[config.accessory];
    if (accOverlay) {
      applyOverlay(grid, accOverlay[direction]);
    }
  }

  return gridToSprite(grid, config);
}

/** All base templates organized by direction */
const BASE_TEMPLATES = {
  down: [DOWN_WALK_0, DOWN_WALK_1, DOWN_WALK_2, DOWN_TYPE_0, DOWN_TYPE_1, DOWN_READ_0, DOWN_READ_1],
  up:   [UP_WALK_0, UP_WALK_1, UP_WALK_2, UP_TYPE_0, UP_TYPE_1, UP_READ_0, UP_READ_1],
  right: [RIGHT_WALK_0, RIGHT_WALK_1, RIGHT_WALK_2, RIGHT_TYPE_0, RIGHT_TYPE_1, RIGHT_READ_0, RIGHT_READ_1],
};

/**
 * Generate a full CharacterSprites set from a CharacterConfig.
 * Produces walk (4 frames), typing (2 frames), and reading (2 frames) per direction.
 * LEFT direction is horizontally flipped from RIGHT.
 */
export function generateCharacterSprites(config: CharacterConfig): CharacterSprites {
  const genDir = (dir: 'down' | 'up' | 'right') => {
    const templates = BASE_TEMPLATES[dir];
    return templates.map(t => generateFrame(t, config, dir));
  };

  const down = genDir('down');
  const up = genDir('up');
  const right = genDir('right');
  const left = right.map(frame => flipSpriteHorizontal(frame));

  return {
    walk: {
      [Direction.DOWN]:  [down[0], down[1], down[2], down[1]] as [SpriteData, SpriteData, SpriteData, SpriteData],
      [Direction.UP]:    [up[0], up[1], up[2], up[1]] as [SpriteData, SpriteData, SpriteData, SpriteData],
      [Direction.RIGHT]: [right[0], right[1], right[2], right[1]] as [SpriteData, SpriteData, SpriteData, SpriteData],
      [Direction.LEFT]:  [left[0], left[1], left[2], left[1]] as [SpriteData, SpriteData, SpriteData, SpriteData],
    },
    typing: {
      [Direction.DOWN]:  [down[3], down[4]] as [SpriteData, SpriteData],
      [Direction.UP]:    [up[3], up[4]] as [SpriteData, SpriteData],
      [Direction.RIGHT]: [right[3], right[4]] as [SpriteData, SpriteData],
      [Direction.LEFT]:  [left[3], left[4]] as [SpriteData, SpriteData],
    },
    reading: {
      [Direction.DOWN]:  [down[5], down[6]] as [SpriteData, SpriteData],
      [Direction.UP]:    [up[5], up[6]] as [SpriteData, SpriteData],
      [Direction.RIGHT]: [right[5], right[6]] as [SpriteData, SpriteData],
      [Direction.LEFT]:  [left[5], left[6]] as [SpriteData, SpriteData],
    },
  };
}

// ════════════════════════════════════════════════════════════════
// Sprite generation cache (keyed by JSON of config)
// ════════════════════════════════════════════════════════════════

const spriteGenCache = new Map<string, CharacterSprites>();

/** Get or generate CharacterSprites for a config. Cached by config JSON. */
export function getCachedCharacterSprites(config: CharacterConfig): CharacterSprites {
  const key = JSON.stringify(config);
  const cached = spriteGenCache.get(key);
  if (cached) return cached;
  const sprites = generateCharacterSprites(config);
  spriteGenCache.set(key, sprites);
  return sprites;
}

/** Clear the generation cache (e.g., after config changes). */
export function clearCharacterTemplateCache(): void {
  spriteGenCache.clear();
}
